import { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Plus } from 'lucide-react';
import { useEntity } from '../../contexts/EntityContext';
import { useAuth } from '../../contexts/AuthContext';
import { api, TransactionFilters } from '../../lib/api';
import { EmptyState } from '../../components/ui/Spinner';
import { Dropdown } from '../../components/ui/Dropdown';
import { NewTransactionModal } from '../../components/cashier/NewTransactionModal';
import { TransactionTable } from '../../components/transactions/TransactionTable';
import { DateFilterControl } from '../../components/transactions/DateFilterControl';
import {
  TX_TYPE_FILTER_OPTIONS,
  type StatusFilter,
} from '../../components/transactions/constants';
import {
  resolveDateInFilter,
  resolveDateOutFilter,
} from '../../components/transactions/dateHelpers';
import { useTransactionListFilters } from '../../components/transactions/useTransactionListFilters';

const PAGE_LIMIT = 20;

export default function CashierTransactionsPage() {
  const { currentEntity } = useEntity();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const f = useTransactionListFilters();

  const [searchInput, setSearchInput] = useState(f.search);
  const [showNewTransactionModal, setShowNewTransactionModal] = useState(false);

  // Sync URL → input (covers back/forward navigation).
  useEffect(() => {
    setSearchInput(f.search);
  }, [f.search]);

  // Debounce input → URL.
  useEffect(() => {
    if (searchInput === f.search) return;
    const t = setTimeout(() => f.setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput, f]);

  const handleTransactionCreated = () => {
    queryClient.invalidateQueries({ queryKey: ['transactions'] });
  };

  const activeFilters: TransactionFilters = useMemo(() => {
    return {
      page: f.page,
      limit: PAGE_LIMIT,
      status: f.status === 'all' ? undefined : f.status,
      currencies: f.currencies.length > 0 ? f.currencies : undefined,
      types: f.types.length > 0 ? f.types : undefined,
      clientSearch: f.search || undefined,
      includeArchived: f.archived || undefined,
      employeeId: f.onlyMine && user ? user.id : undefined,
      ...resolveDateInFilter(f.dateIn),
      ...resolveDateOutFilter(f.dateOut),
    };
  }, [f.page, f.status, f.currencies, f.types, f.search, f.archived, f.onlyMine, f.dateIn, f.dateOut, user]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['transactions', currentEntity?.id, activeFilters],
    queryFn: () => api.transactions.list(currentEntity!.id, activeFilters),
    enabled: !!currentEntity,
  });

  const { data: currenciesData } = useQuery({
    queryKey: ['currencies'],
    queryFn: () => api.admin.currencies.list(),
    enabled: !!currentEntity,
  });

  const currencies = useMemo(() => {
    if (!currenciesData?.data) return [];
    return currenciesData.data
      .filter((c: any) => c.is_active)
      .map((c: any) => ({ value: c.code, label: c.code }))
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  }, [currenciesData]);

  if (!currentEntity) return null;

  const totalPages = data?.meta.totalPages || 1;
  const totalCount = data?.meta.total || 0;
  const displayedCount = data?.data.length || 0;

  return (
    <div className="min-h-screen bg-background p-3 md:p-5">
      <div className="max-w-full mx-auto space-y-3">

        {/* Header */}
        <div className="flex justify-between items-center gap-3 flex-wrap">
          <div className="inline-flex bg-surface rounded-lg p-1 border border-border shadow-sm">
            {(['all', 'open', 'closed'] as StatusFilter[]).map(s => (
              <button
                key={s}
                className={`px-4 py-1.5 rounded-md text-sm font-medium cursor-pointer transition-all ${
                  f.status === s
                    ? 'bg-surface-hover text-foreground shadow-sm'
                    : 'text-foreground-muted hover:text-foreground'
                }`}
                onClick={() => f.setStatus(s)}
              >
                {s === 'all' ? 'Все' : s === 'open' ? 'Открытые' : 'Закрытые'}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Dropdown options={currencies}              selected={f.currencies} onChange={f.setCurrencies} placeholder="Все валюты" multiple />
            <Dropdown options={TX_TYPE_FILTER_OPTIONS}  selected={f.types}      onChange={f.setTypes}      placeholder="Все типы"   multiple />
            <DateFilterControl value={f.dateIn}  onChange={f.setDateIn}  placeholder="Дата IN" />
            <DateFilterControl value={f.dateOut} onChange={f.setDateOut} placeholder="Дата OUT" />
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer px-3 py-2 bg-surface border border-border rounded-lg hover:border-foreground-muted transition-colors">
              <input type="checkbox" checked={f.onlyMine} onChange={e => f.setOnlyMine(e.target.checked)} className="w-4 h-4 accent-ctp-blue" />
              Только мои
            </label>
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer px-3 py-2 bg-surface border border-border rounded-lg hover:border-foreground-muted transition-colors">
              <input type="checkbox" checked={f.archived} onChange={e => f.setArchived(e.target.checked)} className="w-4 h-4 accent-ctp-blue" />
              Архив
            </label>
          </div>
        </div>

        {/* Search + count */}
        <div className="flex justify-between items-center gap-3">
          <div className="relative w-[280px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" />
            <input
              type="text"
              placeholder="Поиск по клиенту, описанию..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="w-full py-2 pl-9 pr-3 bg-surface border border-border rounded-lg text-sm text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-ctp-blue"
            />
          </div>
          <div className="text-xs text-foreground-muted whitespace-nowrap">
            Показано <strong className="text-foreground">{displayedCount}</strong> / {totalCount}
          </div>
        </div>

        {/* Table */}
        <div className="bg-surface rounded-xl shadow-sm border border-border overflow-hidden flex flex-col">
          <TransactionTable
            transactions={data?.data ?? []}
            loading={isLoading}
            error={error ? 'Ошибка загрузки данных' : null}
            emptyState={
              <div className="py-12 flex items-center justify-center">
                <EmptyState
                  title="Нет транзакций"
                  description="По вашему запросу ничего не найдено"
                  action={
                    <button
                      className="flex items-center gap-2 px-4 py-2 bg-ctp-teal text-white border-none rounded-lg text-sm font-semibold cursor-pointer hover:opacity-90"
                      onClick={() => setShowNewTransactionModal(true)}
                    >
                      <Plus size={16} />
                      Новая транзакция
                    </button>
                  }
                />
              </div>
            }
          />

          {!isLoading && !error && (data?.data?.length ?? 0) > 0 && (
            <div className="flex justify-between items-center px-4 py-3 border-t border-border">
              <div className="text-xs text-foreground-muted">Страница {f.page} из {totalPages}</div>
              <div className="flex gap-2">
                <button
                  className="px-3 py-1.5 border border-border rounded-lg bg-surface text-xs font-medium text-foreground-muted transition-all hover:bg-surface-hover hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                  disabled={f.page === 1}
                  onClick={() => f.setPage(f.page - 1)}
                >← Назад</button>
                <button
                  className="px-3 py-1.5 border border-border rounded-lg bg-surface text-xs font-medium text-foreground-muted transition-all hover:bg-surface-hover hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                  disabled={f.page === totalPages}
                  onClick={() => f.setPage(f.page + 1)}
                >Вперёд →</button>
              </div>
            </div>
          )}
        </div>
      </div>

      <NewTransactionModal
        isOpen={showNewTransactionModal}
        onClose={() => setShowNewTransactionModal(false)}
        onSuccess={handleTransactionCreated}
      />
    </div>
  );
}
